/* eslint-disable @typescript-eslint/no-explicit-any */


import { Formik } from 'formik';
import * as Yup from 'yup';
import { EnvelopeIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import Input from '../../components/form-control/Input';
import Link from '../../components/Link';
import Button from '../../components/form-control/Button';
import { SIGN_IN } from '../../resources/links';
import { resetAccountPAssword } from '../../api/requests';
import MessagePrompt from '../../components/elements/MessagePrompt';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HiOutlineMail } from 'react-icons/hi';
import { showToast } from '../../utils/miscFunctions';

function ForgotPassword() {
  const [response, setResponse] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  return (
    <div className="w-full">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
          <HiOutlineMail className="w-8 h-8 text-white" />
        </div>
        <p className="text-gray-600 text-sm leading-relaxed max-w-md mx-auto">
          Enter your email address and we&apos;ll send you a link to reset your password.
        </p>
      </motion.div>

      <Formik
        initialValues={{
          email: '',
          admin: true
        }}
        validateOnChange={false}
        validateOnBlur
        validationSchema={Yup.object({
          email: Yup.string().trim().email('Invalid email').required('Email is required')
        })}
        onSubmit={async (values) => {
          const payload = {
            ...values,
            email: values.email.toLowerCase()
          };

          try {
            const res = await resetAccountPAssword(payload.admin, payload.email);
            setResponse(res?.message);
            setIsSuccess(true);
            showToast.success(res?.message,);
          } catch (error: any) {
            setResponse(error?.description);
            showToast.error(error?.description,);
          }
        }}>
        {({ values, errors, isSubmitting, handleSubmit, handleChange, touched }) => (
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Success Message */}
            <AnimatePresence>
              {response && isSuccess && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="bg-green-50 border border-green-200 rounded-xl p-4"
                >
                  <div className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-medium text-green-800">Check your email</h3>
                      <p className="text-sm text-green-700 mt-1">
                        {response || 'Password reset instructions have been sent to your email.'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error Message */}
            {response && !isSuccess && (
              <MessagePrompt
                type={'error'}
                message={response}
                classes="w-full"
              />
            )}

            {/* Email Input */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Input
                type="email"
                label="Email address"
                name="email"
                required
                wrapperClasses="mb-6"
                value={values.email}
                prepend={<EnvelopeIcon className="w-5 h-5 text-gray-400" />}
                error={touched.email ? errors.email : undefined}
                onChange={handleChange}
                helperText={touched.email ? errors.email : undefined}
                placeholder="Enter your email address"
                showRing={true}
              />
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Button
                type="submit"
                label={isSubmitting ? "Sending reset link..." : "Send reset link"}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-full text-base font-medium text-white bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-gray-500/20 hover:shadow-gray-500/30"
                loading={isSubmitting}
                disabled={isSubmitting}
                fullWidth={true}
                curved={true}
              />
            </motion.div>

            {/* Back to Sign In */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center pt-4"
            >
              <div className="flex items-center justify-center text-sm text-gray-600">
                <ArrowLeftIcon className="h-4 w-4 mr-2 text-gray-400" />
                <span className="mr-1">Remember your password?</span>
                <Link
                  to={SIGN_IN}
                  label="Back to sign in"
                />
              </div>
            </motion.div>
          </form>
        )}
      </Formik>
    </div >
  );
}

export default ForgotPassword;
